import React, {
  useEffect,
  useState,
} from 'react';
import { useStores } from '../../hooks/use-stores';
import './Timeline.css';
import { LoaderInline } from '../PageLoading/PageLoading';
import { SIDEBAR_SCREEN } from '../../stores/sidebar-store';
import { isFeatureEnabled } from '../../utils/feature-flags';

const FEATURE_SCREENSHOT_FOR_REPLAY = isFeatureEnabled('show_screenshot_for_replay');

const Timeline = () => {
  const [ currentIndex, setCurrentIndex ] = useState(0);
  const [ playing, setPlaying ] = useState(false);
  const [ visible, setVisible ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const { batchStore, ticketStore, settingsStore, sidebarStore } = useStores();
  const { configuration } = settingsStore;
  const { batchInfoFrequency } = configuration;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (playing && batchStore.loadHistory.length - 1 > currentIndex) {
        showCurrent(currentIndex + 1);
      } else if (batchStore.loadHistory.length - 1 === currentIndex) {
        stop();
      }
    }, 1000/(batchInfoFrequency || 5));
    return () => {
      clearTimeout(timer);
    };
  });

  const clear = () => {
    stop();
    if (sidebarStore.isOpen(SIDEBAR_SCREEN.BATCH_FORM)) {
      sidebarStore.onToggle(SIDEBAR_SCREEN.BATCH_FORM);
    }
    setCurrentIndex(0);
    setVisible(false);
    batchStore.finishReplayLoad();
  };

  const play = () => {
    if (batchStore.running.length && batchStore.canEmit) {
      return;
    }

    batchStore.onStartReplayLoad(currentIndex);
    batchStore.replayMode = true;

    setPlaying(true);
  };

  const showCurrent = (index) => {
    if (batchStore.replayMode) {
      if (batchStore.loadHistory.length > index) {
        batchStore.stop();
        setCurrentIndex(index);
        batchStore.overrideBatchData(batchStore.loadHistory[index]);
      }
      if (FEATURE_SCREENSHOT_FOR_REPLAY) {
        //show batch form at the beginning of the replay (index < 2)
        if (index < 2) {
          const load = batchStore.loadHistory.find(v => v.running?.length);
          batchStore.orderId = load?.running?.length ? load.running[0]?.id : null;

          if (!sidebarStore.isOpen(SIDEBAR_SCREEN.BATCH_FORM)) {
            sidebarStore.onToggle(SIDEBAR_SCREEN.BATCH_FORM);
          }
        }
        //hide batch form after ~=2 sec, (2x5)=10, batchInfoFrequency~=5
        if (index > 10) {
          if (sidebarStore.isOpen(SIDEBAR_SCREEN.BATCH_FORM)) {
            sidebarStore.onToggle(SIDEBAR_SCREEN.BATCH_FORM);
          }

          batchStore.orderId = null;
        }
      }
    }
  };

  const stop = () => {
    setPlaying(false);
  };

  const onStampClick = (index) => {
    showCurrent(index);
  };

  const toggle = () => {
    setVisible(!visible);
  };

  const displayTime = (index = 0) => {
    const h = batchStore.loadHistory.length;
    const value = index && h > index ? h - (h - index) : h;
    let seconds = Math.floor(value / (batchInfoFrequency || 5)).toFixed(0);
    let minutes = Math.floor(seconds / 60).toFixed(0);
    seconds = seconds < 60 ? seconds : seconds - (minutes * 60);
    seconds = seconds > 9 ? seconds : `0${seconds}`;
    return `${minutes}:${seconds}`;
  };

  const getLogs = () => {
    if (!ticketStore.activeLog) {
      return [];
    }

    return ticketStore.batchingLogs.filter(v => v.batchingDate === ticketStore.activeLog.batchingDate);
  };

  const getLogData = (log) => {
    if (ticketStore.activeLog && ticketStore.activeLog.fileName === log.fileName) {
      return;
    }

    stop();
    setCurrentIndex(0);
    setLoading(true);
    ticketStore.activeLog = log;

    ticketStore.loadBatchingLog(log.fileName, (data) => {
      if (data) {
        batchStore.loadHistory = data;
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <div className="cb-timeline-wrap">
      <button className="btn btn-default btn-timeline-switch"
        onClick={() => toggle()}>
        <i className="fa fa-clock"/>
      </button>
      {visible && (
        <div className="cb-timeline">
          <div className="cb-timeline-controls">
            {!playing
              ? (
                <button className="btn btn-default"
                  onClick={() => play()}>
                  <i className="fa fa-play"/>
                </button>
              )
              : (
                <button className="btn btn-default"
                  onClick={() => stop()}>
                  <i className="fa fa-pause"/>
                </button>
              )}
            <button className="btn btn-default"
              onClick={() => clear()}>
              <i className="fa fa-close"/>
            </button>
          </div>
          <div className="cb-timeline-time">
            {currentIndex ? displayTime(currentIndex) : '0:00'} / {displayTime()}
          </div>
          <div className="cb-timeline-data">
            <input type="range"
              value={currentIndex}
              min={0}
              max={batchStore.loadHistory.length - 1}
              step={1}
              onChange={(e) => onStampClick(Number(e.target.value))}
            />
          </div>
          <div className="cb-timeline-downloads">
            {loading && <LoaderInline/>}
            {getLogs().map((log, i) => (
              <button className="btn btn-default"
                key={i}
                disabled={ticketStore.activeLog.fileName === log.fileName}
                onClick={() => getLogData(log)}>
                <i className="fa fa-download"/>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Timeline;
